import { Globe, Mail, Music2 } from 'lucide-react';

/**
 * Brand glyphs for the platforms the ministry links to.
 *
 * Inline rather than files in `public/icons` so they take `currentColor`
 * (needed for the dark footer and the light pages), cost no extra requests, and
 * stay crisp at any size.
 *
 * The set is deliberately provisioned ahead of the URLs: an editor adds a
 * Social Link row, types the platform name, pastes a URL, and the right icon
 * appears. Rows with no URL never render — see `socialLinks()` below.
 */

type IconProps = { size?: number };
const box = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  'aria-hidden': true as const,
});

/* -------------------------------------------------------------------------- */
/* Social                                                                      */
/* -------------------------------------------------------------------------- */

function Instagram({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function Facebook({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.89h-2.33v6.99A10 10 0 0 0 22 12Z" />
    </svg>
  );
}

function YouTube({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M21.58 7.19a2.5 2.5 0 0 0-1.77-1.77C18.25 5 12 5 12 5s-6.25 0-7.81.42A2.5 2.5 0 0 0 2.42 7.2 26.1 26.1 0 0 0 2 12a26.1 26.1 0 0 0 .42 4.81 2.5 2.5 0 0 0 1.77 1.77C5.75 19 12 19 12 19s6.25 0 7.81-.42a2.5 2.5 0 0 0 1.77-1.77A26.1 26.1 0 0 0 22 12a26.1 26.1 0 0 0-.42-4.81ZM10 15.02V8.98L15.2 12 10 15.02Z" />
    </svg>
  );
}

function TikTok({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1 0-5.18c.27 0 .53.04.77.12v-3.2a5.8 5.8 0 0 0-.77-.05 5.71 5.71 0 1 0 5.71 5.71V9.01a7.35 7.35 0 0 0 4.28 1.37V7.3a4.28 4.28 0 0 1-3.25-1.48Z" />
    </svg>
  );
}

function XTwitter({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M17.53 3h3.06l-6.69 7.64L21.76 21h-6.16l-4.82-6.3L5.26 21H2.2l7.15-8.17L2.24 3H8.5l4.36 5.77L17.53 3Zm-1.07 16.17h1.7L7.62 4.74H5.8l10.66 14.43Z" />
    </svg>
  );
}

function Threads({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <path d="M16.4 11.6c-1.2-.4-2.6-.5-3.9-.3-1.6.3-2.4 1.2-2.2 2.3.2 1 1.2 1.6 2.5 1.4 1.7-.3 2.4-1.8 2.4-4.3 0-2-.9-3.3-2.6-3.5-1.2-.1-2.2.3-2.9 1.2" />
      <path d="M12 21.5c-5.2 0-8.5-3.3-8.5-9.5S6.8 2.5 12 2.5s8.5 3.3 8.5 9.5-3.3 9.5-8.5 9.5Z" />
    </svg>
  );
}

function WhatsApp({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm0 18.02a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23a8.24 8.24 0 0 1 0 16.47Z" />
      <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37s-1.03 1.01-1.03 2.46 1.06 2.86 1.2 3.06c.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.09 1.75-.71 2-1.4.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  );
}

function Telegram({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M21.7 4.3a1 1 0 0 0-1.05-.16L2.9 11.2a1 1 0 0 0 .06 1.87l4.2 1.4 1.6 4.9a1 1 0 0 0 1.66.4l2.3-2.2 4.2 3.1a1 1 0 0 0 1.57-.6l3.5-15a1 1 0 0 0-.29-.77ZM9.5 14.3l-.6 3.1-1.05-3.2 8.2-5.6-6.55 5.7Z" />
    </svg>
  );
}

function LinkedIn({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="currentColor">
      <path d="M4.98 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3 9.5h4V21H3V9.5Zm6.5 0h3.83v1.57h.05a4.2 4.2 0 0 1 3.78-2.07C21 9 22 11.2 22 14.28V21h-4v-6c0-1.43-.03-3.27-2-3.27-2 0-2.3 1.56-2.3 3.17V21h-4V9.5Z" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Listening                                                                   */
/* -------------------------------------------------------------------------- */

function Spotify({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="none">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M7.1 9.3c3.1-.85 6.4-.5 9.05 1M7.9 12.4c2.55-.7 5.3-.4 7.5.85M8.7 15.4c2-.55 4.15-.3 5.9.65"
        stroke="rgb(var(--tr-glass))"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SoundCloud({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M2 14v3M5 12v5M8 10v7M11 8v9" />
      <path d="M14 8.5V17h4.5a3 3 0 0 0 0-6 4 4 0 0 0-4.5-2.5Z" />
    </svg>
  );
}

/** Apple Music: a note inside the rounded square the badge always uses. */
function AppleMusic({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="none">
      <rect x="2.5" y="2.5" width="19" height="19" rx="4.5" fill="currentColor" />
      <path
        d="M10.2 16.1V9.7l5-1.1v5.6"
        stroke="rgb(var(--tr-glass))"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8.9" cy="16.2" r="1.6" fill="rgb(var(--tr-glass))" />
      <circle cx="13.9" cy="14.9" r="1.6" fill="rgb(var(--tr-glass))" />
    </svg>
  );
}

/** Audiomack has no simple mark; a waveform reads correctly and honestly. */
function Audiomack({ size = 18 }: IconProps) {
  return (
    <svg {...box(size)} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M3 12v1M6.5 9v6M10 5.5v13M13.5 8v8M17 10.5v3M20.5 12v1" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Resolution                                                                  */
/* -------------------------------------------------------------------------- */

const ICONS: Record<string, (props: IconProps) => React.ReactNode> = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: YouTube,
  youtubemusic: Music2 as never,
  tiktok: TikTok,
  x: XTwitter,
  twitter: XTwitter,
  threads: Threads,
  whatsapp: WhatsApp,
  telegram: Telegram,
  linkedin: LinkedIn,
  spotify: Spotify,
  soundcloud: SoundCloud,
  applemusic: AppleMusic,
  apple: AppleMusic,
  itunes: AppleMusic,
  playmusic: AppleMusic,
  audiomack: Audiomack,
  boomplay: Audiomack,
  deezer: Audiomack,
  music: (p) => <Music2 size={p.size ?? 18} aria-hidden="true" />,
  email: (p) => <Mail size={p.size ?? 18} aria-hidden="true" />,
  website: (p) => <Globe size={p.size ?? 18} aria-hidden="true" />,
};

/** Every platform the icon set covers, for the CMS placeholder rows. */
export const SUPPORTED_PLATFORMS = [
  'Instagram',
  'Facebook',
  'YouTube',
  'TikTok',
  'X',
  'Threads',
  'WhatsApp',
  'Telegram',
  'LinkedIn',
  'Spotify',
  'Apple Music',
  'Audiomack',
  'SoundCloud',
] as const;

export interface SocialLinkish {
  label?: string;
  platform?: string;
  url?: string;
  icon?: string;
  iconName?: string;
}

/**
 * Resolve a CMS link to an icon.
 *
 * Matches the `icon` key first, then the platform or label, so an editor can
 * simply type "Apple Music" and get the right glyph without knowing icon keys
 * exist. Falls back to a globe rather than rendering nothing.
 */
export function socialIcon(link: SocialLinkish): (props: IconProps) => React.ReactNode {
  const key = (link.icon || link.iconName || link.platform || link.label || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  return ICONS[key] ?? ICONS.website!;
}

/** A readable name for the link, for `aria-label` and tooltips. */
export function socialName(link: SocialLinkish): string {
  return link.platform || link.label || 'Profile';
}

/**
 * Drop the rows an editor has not filled in yet.
 *
 * The CMS is pre-seeded with a row per platform so adding a profile is just
 * pasting a URL. Until then the row must not render — an icon linking nowhere
 * is worse than no icon.
 */
export function socialLinks(...groups: (SocialLinkish[] | undefined | null)[]): SocialLinkish[] {
  const seen = new Set<string>();
  return groups
    .flatMap((group) => group ?? [])
    .filter((link) => {
      const url = link.url?.trim();
      if (!url || url === '/' || url === '#') return false;
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
}
